import * as yup from "yup";
import dayjs from "dayjs";
import { MAX_CHAR_TEXTAREA, substitutionReasonValue } from "./types";
import { ILocation, RentalLocationType } from "../../types";
import { validateLocationDate } from "../../utils";
import { ITALY_CODE, FRANCE_CODE } from "../../../network/config";


export const validationSchema = yup.object({
    eventInfo: yup
        .object({
            reason: yup
                .string()
                .nullable()
                .required("message.validation.required")
            ,
            eventDate: yup
                .date()
                .typeError("message.validation.invalid_date")
                .nullable()
                .required("message.validation.required")
                .test(
                    'testEventDate',
                    'message.validation.date_not_allowed',
                    (value, context) => {
                        const date: dayjs.Dayjs | null = value ? dayjs(value) : null;
                        // @ts-ignore
                        const pickupDateRa = context?.from[1].value?.originRaData?.pickupDate;
                        const tomorrow = dayjs().add(1, 'day')
                        // @ts-ignore
                        const dropoffDateRa = context?.from[1].value?.originRaData?.dropoffDate;

                        return !!date?.isBefore(tomorrow, 'day') && (date?.isAfter(pickupDateRa) || !!date?.isSame(dayjs(pickupDateRa))) && (!!date?.isBefore(dropoffDateRa) || !!date?.isSame(dayjs(dropoffDateRa)));
                    }
                )
            ,
            eventPlaceCountry: yup
                .string()
                .nullable()
                .required("message.validation.required")
            ,
            eventPlaceProvince: yup
                .string()
                .nullable()
                .when('eventPlaceCountry', {
                    is: (value: string) => value === ITALY_CODE,
                    then: (schema) => schema.required("message.validation.required")
                })
            ,
            eventPlaceCity: yup
                .string()
                .nullable()
                .required("message.validation.required")
            ,
            eventPlacePostalCode: yup
                .string()
                .nullable()
                .required("message.validation.required")
            ,
            eventPlaceStreetName: yup
                .string()
                .nullable()
                .required("message.validation.required")
            ,
            eventPlaceStreetNumber: yup
                .string()
                .nullable()
                .required("message.validation.required")
            ,
            eventDescription: yup
                .string()
                .nullable()
                .required("message.validation.required")
                .test(
                    'descriptionLength',
                    '',
                    (value) => {
                        return !value || value.length <= MAX_CHAR_TEXTAREA;
                    }
                )
            ,
            notePoliceInvolved: yup
                .string()
                .nullable()
                .test(
                    'noteRequired',
                    'message.validation.required',
                    (value, context) => {
                        return !(context?.parent?.policeInvolved && !value);
                    }
                )
                .test(
                    'noteLength',
                    '',
                    (value) => {
                        return !value || value.length <= MAX_CHAR_TEXTAREA;
                    }
                ),
        }),
    assets: yup
        .object({
            denunciaFurtoSmarrimento: yup
                .mixed()
                .nullable()
                .test(
                    'testDenunciaFurtoSmarrimentoReq',
                    'message.validation.required',
                    (value, context) => {
                        // @ts-ignore
                        const reason = context?.from[1]?.value?.eventInfo?.reason;
                        return !([substitutionReasonValue.FURTO_VEICOLO].includes(reason) && !value);
                    }
                )
        }),
    carToReplace: yup
        .object({
            dropoffDate: yup
                .date()
                .typeError("message.validation.invalid_date")
                .nullable()
                .test(
                    'testDropoffDateToReplace',
                    'message.validation.date_not_allowed',
                    (value, context) => {
                        // Early return if no vehicle availability
                        // @ts-ignore
                        const vehicleAvailability = context?.from?.[1]?.value?.eventInfo?.vehicleAvailability;
                        if (!vehicleAvailability) {
                            return true;
                        }

                        // Extract all needed values with proper null checking
                        const recoveryMode = context?.options?.context?.recoveryMode;
                        // @ts-ignore
                        const eventInfo = context?.from?.[1]?.value?.eventInfo;
                        // @ts-ignore
                        const originRaData = context?.from?.[1]?.value?.originRaData;
                        const selectedLocation = context.parent?.dropoffLocation;

                        const date = value ? dayjs(value) : null;
                        const dropoffDateRa = originRaData?.dropoffDate;
                        const eventDate = eventInfo?.eventDate;
                        const yesterday = dayjs().subtract(1, 'day');
                        const now = dayjs();

                        // Common validation function
                        const validateLocationAndDate = (date: dayjs.Dayjs, location: ILocation | null) => {
                            return validateLocationDate(date, location, RentalLocationType.Dropoff);
                        };

                        // Both modes require dropoffDateRa and date
                        if (!dropoffDateRa || !date) {
                            return false;
                        }

                        const dropoffRa = dayjs(dropoffDateRa);

                        if (recoveryMode) {

                            // Time-based validation logic
                            const isTimeValid = now.isAfter(dropoffRa)
                                ? (date.isBefore(dropoffRa, 'minute') || date.isSame(dropoffRa, 'minute'))
                                : (date.isBefore(now, 'minute') || date.isSame(now, 'minute'));

                            // Event date validation (dropoffDate >= eventDate)
                            const isAfterEventDate = eventDate
                                ? (date.isAfter(dayjs(eventDate), 'minute') || date.isSame(dayjs(eventDate), 'minute'))
                                : true;

                            return isTimeValid &&
                                   isAfterEventDate &&
                                   validateLocationAndDate(date, selectedLocation);

                        } else {
                            // Non-recovery mode validation
                            const isBeforeOrSameAsRa = date.isBefore(dropoffRa, 'minute') || date.isSame(dropoffRa, 'minute');
                            const isAfterYesterday = date.isAfter(yesterday, 'minute');

                            return isBeforeOrSameAsRa &&
                                   isAfterYesterday &&
                                   validateLocationAndDate(date, selectedLocation);
                        }
                    }
                )
            ,
        }),
    replacementCar: yup
        .object({
            pickupDate: yup
                .date()
                .typeError("message.validation.invalid_date")
                .nullable()
                .required("message.validation.required")
                .test(
                    'testPickupDateReplacement',
                    'message.validation.date_not_allowed',
                    (value, context) => {
                        // @ts-ignore
                        const recoveryMode = context?.options?.context?.recoveryMode;
                        const date: dayjs.Dayjs | null = value ? dayjs(value) : null;
                        // @ts-ignore
                        const dropoffDateRa = context?.from[1].value?.originRaData?.dropoffDate;
                        const yesterday = dayjs().subtract(1, 'day');
                        const selectedLocation: ILocation | null = context.parent.pickupLocation;
                        const dmsAssociatedOperator = context?.options?.context?.dmsAssociatedOperator ?? [];

                        if (recoveryMode) {
                            // @ts-ignore
                            const dropoffDateReplaceRa = context?.from?.[1]?.value?.carToReplace?.dropoffDate;
                            if (!dropoffDateReplaceRa || !date) return false;

                            const dropoff = dayjs(dropoffDateReplaceRa);
                            const today = dayjs().startOf('day');

                            const isBeforeOrSameToday = date.isBefore(today, 'day') || date.isSame(today, 'day');
                            const isAfterOrSameDropoff = date.isAfter(dropoff, 'minute') || date.isSame(dropoff, 'minute');

                            return (
                                isBeforeOrSameToday &&
                                isAfterOrSameDropoff &&
                                validateLocationDate(date, selectedLocation, RentalLocationType.Pickup, dmsAssociatedOperator)
                            );
                        } else {
                            return (!!date?.isBefore(dayjs(dropoffDateRa)) || !!date?.isSame(dayjs(dropoffDateRa)))
                                && (date.isAfter(yesterday, 'day') && validateLocationDate(date, selectedLocation, RentalLocationType.Pickup, dmsAssociatedOperator));
                        }
                    }
                )
                .test(
                    'max30DaysReplacement',
                    'message.validation.dropoff_date_over_30_days_pickup_date',
                    (value, context) => {
                        const date: dayjs.Dayjs | null = value ? dayjs(value) : null;
                        // @ts-ignore
                        const dropoffDateRa = context?.from[1].value?.originRaData?.dropoffDate;
                        if (!date) return true;

                        return dropoffDateRa.isBefore(date.add(30, "day").add(1, 'm'));
                    }
                )
        })
});
